// src/pages/Recommendations.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Typography, 
  CircularProgress, 
  Box, 
  Button,
  Alert,
  IconButton,
  Paper,
  Chip,
  Tooltip,
  Badge,
  Slide,
  Grow,
  useTheme,
  Drawer,
  TextField,
  MenuItem,
  ButtonBase,
  Grid,
  Skeleton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import ProductCard from '../components/ProductCard';
import ShoppingIntentModal from '../components/ShoppingIntentModal';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';
import ThumbDownAltRoundedIcon from '@mui/icons-material/ThumbDownAltRounded';

// Styled components
const CardWrapper = styled(motion.div)({
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  touchAction: 'none',
  '& > *': {
    pointerEvents: 'none',
  }
});

const ActionButton = styled(IconButton)(({ theme, $isLike, $isResetting, $progress = 0 }) => {
  const intensity = Math.abs($progress);
  const isComplete = intensity >= 0.7;
  const borderColor = $isLike ? theme.palette.primary.main : theme.palette.danger.main;
  const progressAngle = isComplete ? 360 : Math.round(Math.min(intensity * 360, 360));

  return {
    width: 'min(70px, 15vw)',
    height: 'min(70px, 15vw)',
    backgroundColor: isComplete
      ? ($isLike ? theme.palette.primary.main : theme.palette.danger.main)
      : 'rgba(255, 255, 255)',
    borderRadius: '50%',
    border: '1px solid #F1F0EE',
    color: isComplete ? '#fff' : ($isLike ? theme.palette.primary.main : theme.palette.danger.main),
    // boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      backgroundColor: isComplete
        ? ($isLike ? theme.palette.primary.main : theme.palette.danger.main)
        : 'rgba(255, 255, 255, 1)',
      // boxShadow: `0 12px 24px ${$isLike 
      //   ? 'rgba(76, 175, 80, 0.4)' 
      //   : 'rgba(244, 67, 54, 0.4)'}`,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.grey[400],
      boxShadow: 'none',
    },
    transition: 'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    willChange: 'transform, background-color, color',
    transform: 'translateZ(0)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -3,
      left: -3,
      right: -3,
      bottom: -3,
      borderRadius: '50%',
      background: intensity === 0 
        ? 'transparent'
        : `conic-gradient(from 0deg at 50% 50%,
            ${borderColor} ${progressAngle}deg,
            transparent ${progressAngle}deg
          )`,
      mask: `
        radial-gradient(farthest-side,
          transparent calc(100% - 4.5px),
          #fff calc(100% - 4px)
        )
      `,
      WebkitMask: `
        radial-gradient(farthest-side,
          transparent calc(100% - 4.5px),
          #fff calc(100% - 4px)
        )
      `,
      // opacity: intensity === 0 ? 0 : isComplete ? 0 : 1,
      transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.2s linear',
      willChange: 'transform, opacity, background',
      WebkitBackfaceVisibility: 'hidden',
      backfaceVisibility: 'hidden',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
    },
    '& svg, & span': {
      fontSize: 'min(32px, 7vw)',
      position: 'relative',
      zIndex: 1,
      transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      willChange: 'transform, color',
      transform: 'translateZ(0)',
    }
  };
});

const StatsBox = styled(Box)(({ theme }) => ({
  background: theme.palette.primary.lighter,
  padding: theme.spacing(1.5, 3),
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const MainContainer = styled(Container)(({ theme }) => ({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1),
  paddingTop: '56px',
  position: 'relative',
  zIndex: 1,
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    paddingTop: '48px',
  },
}));

const CardContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '600px',
  height: 'min(calc(100vh - 180px), 600px)',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1),
  paddingBottom: theme.spacing(10),
  borderRadius: 16,
  background: 'transparent',
  [theme.breakpoints.down('sm')]: {
    height: 'min(calc(100vh - 140px), 500px)',
    paddingBottom: theme.spacing(8),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.7) !important',
  backdropFilter: 'blur(10px)',
  borderRadius: 16,
  border: '1px solid rgba(255, 255, 255, 0.3)',
}));

const BackgroundGradient = styled(Box)(({ $dragProgress = 0 }) => {
  const intensity = Math.abs($dragProgress);
  
  const getGradient = () => {
    if ($dragProgress > 0) {
      // Like - Green gradient from bottom right
      return `linear-gradient(
        to top left,
        rgba(33, 95, 75, ${Math.min(0.8, intensity * 1.2)}) 0%,
        rgba(223, 252, 200, ${Math.min(0.6, intensity * 0.9)}) 30%,
        transparent 60%
      )`;
    } else if ($dragProgress < 0) {
      // Dislike - Red gradient from bottom left
      return `linear-gradient(
        to top right,
        rgba(211, 47, 47, ${Math.min(0.8, intensity * 1.2)}) 0%,
        rgba(255, 235, 238, ${Math.min(0.6, intensity * 0.9)}) 30%,
        transparent 60%
      )`;
    }
    return 'transparent';
  };

  return {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: getGradient(),
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'none',
    zIndex: 0,
  };
});

const MinimalProductCard = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  overflow: 'hidden',
  border: '1px solid #F1F0EE',
  '&:hover': {
    border: '1px solid #F1F0EE',
  },
}));

const RecommendationSkeleton = () => (
  <Box
    sx={{
      position: 'absolute',
      width: 'min(calc(100vw - 32px), 340px)',
      height: 'min(calc(100vh - 280px), 500px)',
      borderRadius: 4,
      overflow: 'hidden',
      backgroundColor: 'background.paper',
      border: '1px solid #F1F0EE',
    }}
  >
    {/* Image skeleton */}
    <Skeleton
      variant="rectangular"
      width="100%"
      height="70%"
      animation="wave"
    />
    
    {/* Content area */}
    <Box sx={{ p: 2 }}>
      {/* Brand name */}
      <Skeleton
        variant="text"
        width="40%"
        height={24}
        animation="wave"
        sx={{ mb: 1 }}
      />
      
      {/* Product name */}
      <Skeleton
        variant="text"
        width="80%"
        height={24}
        animation="wave"
        sx={{ mb: 1 }}
      />
      
      {/* Price */}
      <Skeleton
        variant="text"
        width="30%"
        height={24}
        animation="wave"
      />
    </Box>
  </Box>
);

const Card = ({ product, style, onSwipe, onSwipeProgress }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-30, 30]);
  const [isExiting, setIsExiting] = useState(false);
  
  // Format the product data
  const formattedProduct = {
    id: product.id,
    image: product.media?.images?.find(img => img.isPrimary)?.url || product.media?.images?.[0]?.url,
    brand: product.brand?.name || '',
    name: product.title || '',
    price: product.price,
    originalPrice: product.original_price,
  };

  // Optimize drag progress calculation with debounce
  useEffect(() => {
    let lastRAF;
    let lastProgress = 0;
    let lastUpdateTime = 0;
    const UPDATE_INTERVAL = 16; // ~60fps
    const THRESHOLD = 0.02; // Increased threshold slightly

    const unsubscribe = x.onChange((latest) => {
      const now = performance.now();
      
      // Throttle updates to maintain consistent frame rate
      if (now - lastUpdateTime < UPDATE_INTERVAL) {
        return;
      }

      if (lastRAF) {
        cancelAnimationFrame(lastRAF);
      }
      
      lastRAF = requestAnimationFrame(() => {
        const absX = Math.abs(latest);
        const isMobile = window.innerWidth < 768;
        const startThreshold = isMobile ? 10 : 20;
        const maxDistance = isMobile ? 100 : 120;
        
        if (absX > startThreshold) {
          const rawProgress = (absX - startThreshold) / (maxDistance - startThreshold);
          const progress = Math.min(rawProgress, 1);
          const newProgress = latest > 0 ? progress : -progress;
          
          // Only update if change is significant enough
          if (Math.abs(newProgress - lastProgress) > THRESHOLD) {
            lastProgress = newProgress;
            lastUpdateTime = now;
            
            // Round progress to reduce jitter
            const roundedProgress = Math.round(newProgress * 100) / 100;
            product.dragProgress = roundedProgress;
            
            onSwipeProgress({
              progress: roundedProgress,
              isResetting: false
            });
          }
        } else {
          // Ensure progress is completely reset when below threshold
          lastProgress = 0;
          lastUpdateTime = now;
          product.dragProgress = 0;
          onSwipeProgress({
            progress: 0,
            isResetting: true
          });
        }
      });
    });
    
    return () => {
      unsubscribe();
      if (lastRAF) {
        cancelAnimationFrame(lastRAF);
      }
    };
  }, [x, onSwipeProgress, product]);

  // Custom opacity transform that considers both swipe distance and exit state
  const opacity = useTransform(
    x, 
    [-200, -150, 0, 150, 200], 
    isExiting ? [0, 0, 0, 0, 0] : [0, 1, 1, 1, 0]
  );
  
  // Get window width for responsive distances
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate thresholds based on screen size
  const swipeThreshold = windowWidth < 768 ? 40 : 60; // Reduced threshold for swipe completion
  const colorThreshold = windowWidth < 768 ? 20 : 30; // Earlier color change
  const maxSwipeDistance = windowWidth < 768 ? 100 : 120; // Match with maxDistance above

  const dragEndHandler = (event, info) => {
    const intensity = Math.abs(info.offset.x);
    const isMobile = window.innerWidth < 768;
    const startThreshold = isMobile ? 10 : 20;
    const maxDistance = isMobile ? 100 : 120;
    
    // Calculate progress to check if circle is complete
    const rawProgress = (intensity - startThreshold) / (maxDistance - startThreshold);
    const progress = Math.min(rawProgress, 1);
    
    if (progress >= 0.7) {
      const direction = info.offset.x > 0 ? 'right' : 'left';
      const isLiked = direction === 'right';
      
      setIsExiting(true);
      
      product.dragProgress = isLiked ? 1 : -1;
      onSwipeProgress({
        progress: isLiked ? 1 : -1,
        isResetting: true
      });

      setTimeout(() => {
        onSwipe(direction, product);
      }, 300);
    } else {
      // Immediately reset progress to 0
      product.dragProgress = 0;
      onSwipeProgress({
        progress: 0,
        isResetting: true
      });
      setIsExiting(false);
    }
  };

  return (
    <CardWrapper
      style={{ 
        x, 
        y, 
        rotate, 
        opacity,
        height: 'min(calc(100vh - 280px), 500px)',
        width: 'min(calc(100vw - 32px), 340px)',
        margin: '0 auto',
        willChange: 'transform',
        transform: 'translateZ(0)',
        borderRadius: 4,
        ...style 
      }}
      drag
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={1}
      onDragEnd={dragEndHandler}
      whileTap={{ cursor: 'grabbing' }}
      initial={{ opacity: 1 }}
      animate={{ opacity: isExiting ? 0 : 1 }}
      exit={{ opacity: 0 }}
      transition={{ opacity: { duration: 0.2 } }}
    >
      <MinimalProductCard>
        {/* Image Container */}
        <Box 
          sx={{ 
            position: 'relative',
            width: '100%',
            height: '75%',
            overflow: 'hidden',
            backgroundColor: 'grey.100'
          }}
        >
          <Box
            component="img"
            src={formattedProduct.image}
            alt={formattedProduct.name}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </Box>

        {/* Content Container */}
        <Box 
          sx={{ 
            p: 2,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            background: 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 100%)',
          }}
        >
          {/* Brand and Name */}
          <Box>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                color: 'text.secondary',
                mb: 0.5,
                fontWeight: 500
              }}
            >
              {formattedProduct.brand}
            </Typography>
            <Typography 
              variant="body1"
              sx={{ 
                fontWeight: 'medium',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {formattedProduct.name}
            </Typography>
          </Box>

          {/* Price */}
          <Box sx={{ 
            mt: 1, 
            display: 'flex', 
            alignItems: 'center',
            gap: 1
          }}>
            <Typography 
              variant="h6" 
              component="span"
              sx={{ 
                fontSize: '1.1rem',
                fontWeight: 600
              }}
            >
              ${formattedProduct.price}
            </Typography>
            {formattedProduct.originalPrice && formattedProduct.originalPrice > formattedProduct.price && (
              <>
                <Typography 
                  variant="body2" 
                  component="span"
                  sx={{ 
                    textDecoration: 'line-through',
                    color: 'text.secondary',
                    fontSize: '0.875rem'
                  }}
                >
                  ${formattedProduct.originalPrice}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    color: 'error.main',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  {Math.round(((formattedProduct.originalPrice - formattedProduct.price) / formattedProduct.originalPrice) * 100)}% OFF
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </MinimalProductCard>
    </CardWrapper>
  );
};

// Update the StatsFooter styled component
const StatsFooter = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(10),
  width: '90%',
  maxWidth: '540px', // 90% of 600px
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(8px)',
  padding: theme.spacing(1.5, 6),
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '80px',
  border: '1px solid #F1F0EE',
  zIndex: 1500,
}));

const StatBox = styled(Box)(({ theme }) => ({
  background: theme.palette.primary.lighter,
  padding: theme.spacing(1.5, 3),
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

function Recommendations() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [likedIndices, setLikedIndices] = useState([]);
  const [dislikedIndices, setDislikedIndices] = useState([]);
  const [offset, setOffset] = useState(0);
  const [isProcessingSwipe, setIsProcessingSwipe] = useState(false);
  const [showIntentModal, setShowIntentModal] = useState(false);
  const [activeIntent, setActiveIntent] = useState(null);
  const BATCH_SIZE = 10;
  const MIN_PRODUCTS = 5;
  const REFRESH_AFTER_LIKES = 5;
  
  const currentCardRef = useRef(null);
  const { api, user } = useAuth();
  const navigate = useNavigate();

  const [userStats, setUserStats] = useState({
    total_liked: 0,
    total_disliked: 0
  });

  const [swipeDirection, setSwipeDirection] = useState(null);
  const [swipeProgress, setSwipeProgress] = useState({ 
    buttonScale: 1, 
    backgroundProgress: 0,
    isResetting: false 
  });

  const [backgroundProgress, setBackgroundProgress] = useState(0);

  // Add a placeholder image
  const placeholderImage = 'path/to/placeholder/image.jpg';

  const swiperRef = useRef(null);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!user) {
      navigate('/auth');
      return;
    }
    
    // Fetch initial user preferences and shopping intent
    fetchUserPreferences();
    fetchShoppingIntent();
  }, [user]);

  const fetchShoppingIntent = async () => {
    try {
      const response = await api.get('/api/shopping-intent');
      if (response.data.shopping_intent) {
        setActiveIntent(response.data.shopping_intent);
        setShowIntentModal(false);
      }
    } catch (error) {
      console.error('Error fetching shopping intent:', error);
    }
  };

  const handleSetShoppingIntent = async (intent) => {
    try {
      const formattedIntent = {
        ...intent,
        requirements: {
          colors: intent.colors,
          style: intent.style,
          occasion: intent.occasion,
          max_price: intent.max_price
        }
      };
      await api.post('/api/shopping-intent', formattedIntent);
      setActiveIntent(formattedIntent);
      // Refresh products with new intent
      await fetchProducts([], [], true);
    } catch (error) {
      console.error('Error setting shopping intent:', error);
      setError('Failed to set shopping preferences');
    }
  };

  const handleClearIntent = async () => {
    try {
      await api.delete('/api/shopping-intent');
      setActiveIntent(null);
      // Refresh products without intent
      await fetchProducts(likedIndices, dislikedIndices, true);
    } catch (error) {
      console.error('Error clearing shopping intent:', error);
    }
  };

  const handleRemoveFilter = async (filterType) => {
    try {
      if (!activeIntent?.requirements) return;

      const updatedRequirements = { ...activeIntent.requirements };
      delete updatedRequirements[filterType];

      const updatedIntent = {
        ...activeIntent,
        requirements: updatedRequirements
      };

      await api.post('/api/shopping-intent', updatedIntent);
      setActiveIntent(updatedIntent);
      await fetchProducts(likedIndices, dislikedIndices, true);
    } catch (error) {
      console.error('Error removing filter:', error);
    }
  };

  const fetchUserPreferences = async () => {
    if (!user?._id) {
      console.error('No user ID available');
      navigate('/auth');
      return;
    }

    try {
      const response = await api.get(`/api/users/${user._id}/preferences`);
      const prefs = response.data.preferences;
      
      // Set the initial state from database
      setLikedIndices(prefs.liked_products || []);
      setDislikedIndices(prefs.disliked_products || []);
      setUserStats({
        total_liked: prefs.liked_products?.length || 0,
        total_disliked: prefs.disliked_products?.length || 0
      });

      // Fetch initial products with the database values
      await fetchProducts(prefs.liked_products || [], prefs.disliked_products || [], true);
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      if (error.response?.status === 404) {
        // If preferences don't exist, initialize them
        await initializeUserPreferences();
      } else if (error.response?.status === 403) {
        // If unauthorized, redirect to auth
        navigate('/auth');
      } else {
        // For other errors, start fresh
        fetchProducts([], [], true);
      }
    }
  };

  const initializeUserPreferences = async () => {
    if (!user?._id) {
      console.error('No user ID available');
      navigate('/auth');
      return;
    }

    try {
      await api.put(`/api/users/${user._id}/preferences`, {
        liked_products: [],
        disliked_products: []
      });
      // Start fresh after initializing
      await fetchProducts([], [], true);
    } catch (error) {
      console.error('Error initializing preferences:', error);
      if (error.response?.status === 403) {
        navigate('/auth');
      }
    }
  };

  const fetchProducts = async (newLikedIndices = likedIndices, newDislikedIndices = dislikedIndices, resetOffset = false) => {
    try {
      setLoading(true);
      setError(null);

      const newOffset = resetOffset ? 0 : offset;
      const queryParams = new URLSearchParams({
        offset: newOffset,
        limit: BATCH_SIZE,
        liked_indices: newLikedIndices.join(','),
        disliked_indices: newDislikedIndices.join(','),
      });

      // Add shopping intent filters if active
      if (activeIntent?.requirements) {
        if (activeIntent.requirements.colors) {
          queryParams.append('colors', activeIntent.requirements.colors.join(','));
        }
        if (activeIntent.requirements.max_price) {
          queryParams.append('max_price', activeIntent.requirements.max_price);
        }
        if (activeIntent.requirements.style) {
          queryParams.append('style', activeIntent.requirements.style);
        }
      }

      const response = await api.get(`/api/products?${queryParams}`);
      
      if (resetOffset) {
        setProducts(response.data.products || []);
      } else {
        setProducts(prevProducts => [...prevProducts, ...(response.data.products || [])]);
      }

      setOffset(newOffset + BATCH_SIZE);
      
      // Update user stats if provided
      if (response.data.user_stats) {
        setUserStats(response.data.user_stats);
      }

    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateUserPreferences = async (newLikedIndices, newDislikedIndices) => {
    if (!user?._id) return;

    try {
      await api.put(`/api/users/${user._id}/preferences`, {
        liked_products: newLikedIndices,
        disliked_products: newDislikedIndices
      });
    } catch (error) {
      console.error('Error updating preferences:', error);
      setError('Failed to save your preferences');
    }
  };

  const handleSwipe = async (direction, product) => {
    if (!product) return;

    try {
      const isLiked = direction === 'right';
      const productId = product.id;

      let newLikedIndices = [...likedIndices];
      let newDislikedIndices = [...dislikedIndices];

      if (isLiked) {
        newLikedIndices = [...newLikedIndices, productId];
        setLikedIndices(newLikedIndices);
        setUserStats(prev => ({
          ...prev,
          total_liked: (prev.total_liked || 0) + 1
        }));
      } else {
        newDislikedIndices = [...newDislikedIndices, productId];
        setDislikedIndices(newDislikedIndices);
        setUserStats(prev => ({
          ...prev,
          total_disliked: (prev.total_disliked || 0) + 1
        }));
      }

      await updateUserPreferences(newLikedIndices, newDislikedIndices);
      setProducts(prevProducts => prevProducts.filter(p => p.id !== productId));

      if (products.length <= MIN_PRODUCTS) {
        await fetchProducts(newLikedIndices, newDislikedIndices);
      }

      if (newLikedIndices.length % REFRESH_AFTER_LIKES === 0) {
        await fetchProducts(newLikedIndices, newDislikedIndices, true);
      }

    } catch (error) {
      console.error('Error handling swipe:', error);
      setError('Failed to process your choice');
    } finally {
      // Ensure we reset all states properly
      setTimeout(() => {
        setSwipeProgress({
          buttonScale: 1,
          backgroundProgress: 0,
          isResetting: false
        });
        setIsProcessingSwipe(false);
      }, 100);
    }
  };

  const handleButtonSwipe = async (isLiked) => {
    if (isProcessingSwipe || products.length === 0) return;
    setIsProcessingSwipe(true);
    
    // First show full swipe state
    setSwipeProgress({
      buttonScale: isLiked ? 1.3 : -1.3,
      backgroundProgress: isLiked ? 1 : -1,
      isResetting: false
    });

    // Then trigger reset animation
    setTimeout(() => {
      setSwipeProgress({
        buttonScale: 1,
        backgroundProgress: 0,
        isResetting: true
      });
    }, 150);

    // Finally handle the swipe
    setTimeout(() => {
      handleSwipe(isLiked ? 'right' : 'left', products[0]);
    }, 300);
  };

  const handleStartOver = async () => {
    setLikedIndices([]);
    setDislikedIndices([]);
    setOffset(0);
    await updateUserPreferences([], []);
    await fetchProducts([], [], true);
  };

  // Ensure swipeDirection is reset after swipe
  useEffect(() => {
    if (swipeDirection) {
      const timer = setTimeout(() => {
        setSwipeDirection(null);
        setIsProcessingSwipe(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [swipeDirection]);

  // Temporarily set products to empty and loading to true for testing
  useEffect(() => {
    setProducts([]);
    setLoading(true);
  }, []);

  const handleSwipeProgress = (progress) => {
    setSwipeProgress(progress);
    setBackgroundProgress(progress.progress || 0);
  };

  const renderCard = (product) => {
    if (!product) return null;

    const formattedProduct = {
      id: product.id,
      image: product.media?.images?.find(img => img.isPrimary)?.url || product.media?.images?.[0]?.url,
      brand: product.brand?.name || '',
      name: product.title || '',
      price: product.price,
      originalPrice: product.original_price,
      discountPercentage: product.discount_percentage,
      productDetails: product.product_details || {},
      variants: product.variants || [],
      category: product.product_details?.productType?.name || product.category || '',
      color: product.variants?.[0]?.color || product.color || '',
      styleScores: product.style_scores || {},
    };
    
    return (
      <Box sx={{ 
        width: 'min(calc(100vw - 32px), 340px)',
        height: 'min(calc(100vh - 280px), 500px)',
        margin: '0 auto',
        borderRadius: 16,
        // border: '1px solid rgba(0, 0, 0, 0.12)',
        transition: 'transform 0.3s ease',
        backgroundColor: theme => theme.palette.grey[100],
        '&:hover': {
          transform: 'scale(1.02)',
          // borderColor: 'rgba(0, 0, 0, 0.2)',
        },
      }}>
        <ProductCard {...formattedProduct} />
      </Box>
    );
  };

  return (
    <>
      <BackgroundGradient $dragProgress={backgroundProgress} />
      <MainContainer maxWidth="sm">

        {error && (
          <Grow in={true}>
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2, 
                width: '100%',
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(244, 67, 54, 0.5)',
              }}
            >
              {error}
            </Alert>
          </Grow>
        )}

        <Box sx={{ mt: 2,mb: 2, textAlign: 'center' }}>
          <Typography 
            variant="h4" 
            sx={(theme) => ({ 
              color: theme.palette.primary.main, 
              mb: 1,
              fontWeight: 600,
              fontSize: {
                xs: '1.5rem',
                sm: '2rem',
                md: '2.125rem',
              },
            })}
          >
            Would you wear that?
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
            Imagine yourself in this
          </Typography>
        </Box>

        <CardContainer>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '200px',
              flexDirection: 'column',
              gap: 2,
              position: 'relative',
              width: '100%',
              height: '100%'
            }}>
              <RecommendationSkeleton />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Finding Perfect Matches For You...
              </Typography>
            </Box>
          ) : products.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              gap: 2
            }}>
              <Typography variant="h6">
                No More Products To Show
              </Typography>
              <Button
                variant="outlined"
                onClick={handleStartOver}
                startIcon={<RestartAltIcon />}
                sx={{
                  borderRadius: 3,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  }
                }}
              >
                Start Over
              </Button>
            </Box>
          ) : (
            <AnimatePresence>
              {products.slice(0, 3).map((product, index) => (
                <Card
                  key={`${product.id}-${index}`}
                  product={product}
                  style={{
                    zIndex: products.length - index,
                  }}
                  onSwipe={handleSwipe}
                  onSwipeProgress={handleSwipeProgress}
                />
              ))}
            </AnimatePresence>
          )}

          {products.length > 0 && (
            <Box sx={(theme) => ({ 
              position: 'fixed', 
              bottom: theme.spacing(20), // Increased from 10 to 20 to move up above the stats footer
              left: 0, 
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              gap: 3,
              padding: theme.spacing(1.5),
              zIndex: 1200,
              pointerEvents: 'none',
              '& > button': {
                pointerEvents: 'auto'
              }
            })}>
              <ActionButton
                color="dislike"
                onClick={() => handleButtonSwipe(false)}
                disabled={isProcessingSwipe}
                $isLike={false}
                $isResetting={swipeProgress.isResetting}
                $progress={swipeProgress.progress < 0 ? -swipeProgress.progress : 0}
                aria-label="Dislike"
              >
                👎
              </ActionButton>
              <ActionButton
                color="like"
                onClick={() => handleButtonSwipe(true)}
                disabled={isProcessingSwipe}
                $isLike={true}
                $isResetting={swipeProgress.isResetting}
                $progress={swipeProgress.progress > 0 ? swipeProgress.progress : 0}
                aria-label="Like"
              >
                👍
              </ActionButton>
            </Box>
          )}

          {products.length > 0 && (
            <StatsFooter>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  color: 'primary.main',
                  maxWidth: '600px',
                  fontWeight: 500,
                  fontSize: {
                    xs: '1rem',
                    sm: '1.25rem',
                    md: '1.5rem',
                  },
                }}
              >
                Your votes 🔥
              </Typography>
              <StatBox>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'primary.main',
                    fontWeight: 600,
                    lineHeight: 1
                  }}
                >
                  {userStats.total_liked}
                </Typography>
              </StatBox>
              
            </StatsFooter>
            
          )}
        </CardContainer>

        <ShoppingIntentModal
          open={showIntentModal}
          onClose={() => setShowIntentModal(false)}
          onSubmit={handleSetShoppingIntent}
          onClear={handleClearIntent}
          currentIntent={activeIntent}
        />
      </MainContainer>
    </>
  );
}

export default Recommendations;